import { createSelector, createSlice } from "@reduxjs/toolkit";
import { createFinalFICMeetingThunk, fetchFinalFICMeetingThunk, updateFinalFICMeetingThunk } from "./thunks";
import { FinalFICDecision } from "../../../../api/finalFIC";
import { RootState } from "../../../../app/store";



interface FinalFICState {
  status: "idle" | "loading" | "failed";
  error: string | null;

  convener?: string;
  name_of_committee?: string;
  members_of_committee: string[];
  committee_decision?: FinalFICDecision;
  fraud_classification?: string;
  review_committee_remarks?: string;
}

const initialState: FinalFICState = {
  status: "idle",
  error: null,
  convener: undefined,
  name_of_committee: undefined,
  members_of_committee: [],
  committee_decision: undefined,
  fraud_classification: undefined,
  review_committee_remarks: undefined,
};

const finalFICSlice = createSlice({
  name: "fic",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      /** ================= FETCH ================= */
      .addCase(fetchFinalFICMeetingThunk.pending, (state) => {
         return {
            ...initialState,
            status: "loading",
            error: null,
        };
      })

      .addCase(fetchFinalFICMeetingThunk.fulfilled, (state, action) => {
        const report = action.payload.report;
        state.status = "idle";
        state.error = null;

        if (!report) return;

        state.convener = report?.convener;
        state.name_of_committee = report?.name_of_committee;
        state.members_of_committee = report?.members_of_committee || [];
        state.committee_decision = report?.committee_decision;
        state.fraud_classification = report?.fraud_classification;
        state.review_committee_remarks = report?.review_committee_remarks;
      })

      .addCase(fetchFinalFICMeetingThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload || "Failed to fetch Final FIC meeting";
      })

      .addCase(createFinalFICMeetingThunk.fulfilled, (state, action) => {
        const report = action.payload.report;
        if (!report) return;
        state.convener = report?.convener;
        state.name_of_committee = report?.name_of_committee;
        state.members_of_committee = report?.members_of_committee || [];
        state.committee_decision = report?.committee_decision;
        state.fraud_classification = report?.fraud_classification;
        state.review_committee_remarks = report?.review_committee_remarks;
      })

      .addCase(updateFinalFICMeetingThunk.fulfilled, (state, action) => {
        const request = action.meta.arg.request;
        state.convener = request?.convener;
        state.name_of_committee = request?.name_of_committee;
        state.members_of_committee = request?.members_of_committee || [];
        state.committee_decision = request?.committee_decision;
        state.fraud_classification = request?.fraud_classification;
        state.review_committee_remarks = request?.review_committee_remarks;
      });
  },
});

export const finalFICReducer = finalFICSlice.reducer;


export const selectFinalFICState = (state: RootState) =>
  state.fic;

export const selectFinalFICStatus = (state: RootState) =>
  state.fic.status;

export const selectFinalFICError = (state: RootState) =>
  state.fic.error;

export const selectFinalFICData = createSelector(
  (state: RootState) => state.fic,
  (fic) => ({
    convener: fic.convener,
    name_of_committee: fic.name_of_committee,
    members_of_committee: fic.members_of_committee,
    committee_decision: fic.committee_decision,
    fraud_classification: fic.fraud_classification,
    review_committee_remarks: fic.review_committee_remarks,
  })
);
export const selectIsFinalFICCreated = (state: RootState) =>
  !!state.fic.committee_decision;